import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useClinic, useUpdateClinicSettings } from '@/hooks/useClinicSettings';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Select } from '@/components/ui/Select';
import { StatusChip } from '@/components/ui/Chip';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  FieldWithIcon,
  PlainField,
  SaveBar,
  SectionCard,
  SettingsFormGrid,
  SettingsHint,
  SettingsLoading,
} from './settingsChrome';
import type { Json } from '@/types/database.generated';
import {
  parseInvoiceCancellationPolicy,
  type InvoiceCancellationPolicy,
  type InvoicePaidAction,
  type InvoicePartialAction,
  type InvoiceUnpaidAction,
} from '@/lib/bookingPolicies';

const CURRENCIES = ['DKK', 'EUR', 'SEK', 'NOK', 'USD'] as const;
const LOCALES = ['da', 'en', 'de', 'sv', 'no'] as const;

export function ClinicBilling() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();
  const { data: clinic, isLoading, error } = useClinic(activeClinicId);
  const update = useUpdateClinicSettings(activeClinicId);

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const [vatNumber, setVatNumber] = useState('');
  const [vatRate, setVatRate] = useState('');
  const [currency, setCurrency] = useState<string>('DKK');
  const [locale, setLocale] = useState<string>('da');
  const [paymentTermsDays, setPaymentTermsDays] = useState('');
  const [invoicePrefix, setInvoicePrefix] = useState('');
  const [invoiceStartNumber, setInvoiceStartNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [regNumber, setRegNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [invoicePolicy, setInvoicePolicy] = useState<InvoiceCancellationPolicy>({
    unpaid: { action: 'leave' },
    paid: { action: 'no_refund' },
    partial: { action: 'no_refund' },
  });

  useEffect(() => {
    if (!clinic) return;
    setVatNumber(clinic.vat_number ?? '');
    setVatRate(
      clinic.vat_rate_percent != null
        ? String(clinic.vat_rate_percent)
        : clinic.default_vat_rate_percent != null
          ? String(clinic.default_vat_rate_percent)
          : '',
    );
    setCurrency(clinic.default_currency);
    setLocale(clinic.default_locale);
    setPaymentTermsDays(clinic.payment_terms_days != null ? String(clinic.payment_terms_days) : '14');
    setInvoicePrefix(clinic.invoice_prefix ?? 'FAK');
    setInvoiceStartNumber(clinic.invoice_start_number != null ? String(clinic.invoice_start_number) : '1000');
    
    const bankInfo = (clinic.bank_info as Record<string, unknown>) ?? {};
    setBankName(String(bankInfo.bank ?? ''));
    setRegNumber(String(bankInfo.reg_number ?? ''));
    setAccountNumber(String(bankInfo.account_number ?? ''));
    setInvoicePolicy(
      parseInvoiceCancellationPolicy(
        (clinic as { invoice_cancellation_policy?: unknown }).invoice_cancellation_policy,
      ),
    );
  }, [clinic]);

  if (isLoading) {
    return (
      <SettingsLoading />
    );
  }
  if (error) return <ErrorAlert message={t('settings.load_error')} />;
  if (!clinic) return <ErrorAlert message={t('settings.no_clinic')} />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload: Record<string, Json> = {
      vat_number: vatNumber.trim() || null,
      default_currency: currency,
      default_locale: locale,
    };
    if (vatRate.trim() === '') {
      payload.vat_rate_percent = null;
    } else {
      const n = Number(vatRate);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        toast.error(t('settings.save_error_toast'));
        return;
      }
      payload.vat_rate_percent = n;
    }
    
    // Payment terms days
    if (paymentTermsDays.trim() !== '') {
      const days = Number(paymentTermsDays);
      if (!Number.isFinite(days) || days < 0 || days > 365) {
        toast.error(t('settings.billing.payment_terms_error'));
        return;
      }
      payload.payment_terms_days = days;
    }

    // Invoice prefix
    if (invoicePrefix.trim() !== '') {
      payload.invoice_prefix = invoicePrefix.trim().toUpperCase();
    }

    // Invoice start number
    if (invoiceStartNumber.trim() !== '') {
      const startNum = Number(invoiceStartNumber);
      if (!Number.isFinite(startNum) || startNum < 1) {
        toast.error(t('settings.billing.invoice_start_number_error'));
        return;
      }
      payload.invoice_start_number = startNum;
    }
    
    const bankInfo: Record<string, string> = {};
    if (bankName.trim()) bankInfo.bank = bankName.trim();
    if (regNumber.trim()) bankInfo.reg_number = regNumber.trim();
    if (accountNumber.trim()) bankInfo.account_number = accountNumber.trim();
    payload.bank_info = bankInfo as Json;
    payload.invoice_cancellation_policy = invoicePolicy as unknown as Json;
    
    try {
      await update.mutateAsync(payload);
      toast.success(t('settings.saved_toast'));
    } catch (err) {
      console.error('update_clinic_settings (billing) failed', err);
      toast.error(t('settings.save_error_toast'));
    }
  };

  const subscriptionStatus = clinic.subscription_status;

  return (
    <form onSubmit={onSubmit} className="flex min-w-0 flex-col gap-s4">
      <SectionCard
        title={t('settings.billing.title')}
        subtitle={t('settings.billing.subtitle')}
        readOnly={!canEdit}
      >
        <SettingsFormGrid className="mt-0">
          <FieldWithIcon
            id="vat-number"
            label={t('settings.billing.vat_number')}
            icon={CreditCard}
            value={vatNumber}
            onChange={setVatNumber}
            disabled={!canEdit}
          />
          <PlainField
            id="vat-rate"
            label={t('settings.billing.vat_rate')}
            value={vatRate}
            onChange={setVatRate}
            type="number"
            disabled={!canEdit}
          />
          <Select
            id="default-currency"
            label={t('settings.billing.default_currency')}
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            disabled={!canEdit}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          <Select
            id="default-locale"
            label={t('settings.billing.default_locale')}
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            disabled={!canEdit}
          >
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {l.toUpperCase()}
              </option>
            ))}
          </Select>
        </SettingsFormGrid>
      </SectionCard>

      <SectionCard
        title={t('settings.billing.invoice_settings_title')}
        subtitle={t('settings.billing.invoice_settings_subtitle')}
        readOnly={!canEdit}
      >
        <SettingsFormGrid className="mt-0 nav:grid-cols-3">
          <PlainField
            id="payment-terms-days"
            label={t('settings.billing.payment_terms_days')}
            value={paymentTermsDays}
            onChange={setPaymentTermsDays}
            type="number"
            disabled={!canEdit}
            placeholder="14"
          />
          <PlainField
            id="invoice-prefix"
            label={t('settings.billing.invoice_prefix')}
            value={invoicePrefix}
            onChange={setInvoicePrefix}
            disabled={!canEdit}
            placeholder="FAK"
          />
          <PlainField
            id="invoice-start-number"
            label={t('settings.billing.invoice_start_number')}
            value={invoiceStartNumber}
            onChange={setInvoiceStartNumber}
            type="number"
            disabled={!canEdit}
            placeholder="1000"
          />
        </SettingsFormGrid>
      </SectionCard>

      <SectionCard
        title={t('settings.billing.bank_info_title')}
        subtitle={t('settings.billing.bank_info_subtitle')}
        readOnly={!canEdit}
      >
        <SettingsFormGrid className="mt-0">
          <PlainField
            id="bank-name"
            label={t('settings.billing.bank_name')}
            value={bankName}
            onChange={setBankName}
            disabled={!canEdit}
            placeholder={t('settings.billing.bank_name_placeholder')}
          />
          <PlainField
            id="reg-number"
            label={t('settings.billing.reg_number')}
            value={regNumber}
            onChange={setRegNumber}
            disabled={!canEdit}
            placeholder={t('settings.billing.reg_number_placeholder')}
          />
          <PlainField
            id="account-number"
            label={t('settings.billing.account_number')}
            value={accountNumber}
            onChange={setAccountNumber}
            disabled={!canEdit}
            placeholder={t('settings.billing.account_number_placeholder')}
          />
        </SettingsFormGrid>
      </SectionCard>

      <SectionCard
        title={t('booking_policies.invoice_policy_title')}
        subtitle={t('booking_policies.invoice_policy_subtitle')}
        readOnly={!canEdit}
      >
        <SettingsFormGrid className="mt-0 nav:grid-cols-3">
          <Select
            id="invoice-unpaid"
            label={t('booking_policies.invoice_unpaid')}
            value={invoicePolicy.unpaid.action}
            disabled={!canEdit}
            onChange={(e) =>
              setInvoicePolicy((p) => ({
                ...p,
                unpaid: { action: e.target.value as InvoiceUnpaidAction },
              }))
            }
          >
            <option value="leave">{t('booking_policies.invoice_leave')}</option>
            <option value="cancel">{t('booking_policies.invoice_cancel')}</option>
            <option value="void">{t('booking_policies.invoice_void')}</option>
          </Select>
          <Select
            id="invoice-paid"
            label={t('booking_policies.invoice_paid')}
            value={invoicePolicy.paid.action}
            disabled={!canEdit}
            onChange={(e) =>
              setInvoicePolicy((p) => ({
                ...p,
                paid: { action: e.target.value as InvoicePaidAction },
              }))
            }
          >
            <option value="no_refund">{t('booking_policies.action_no_refund')}</option>
            <option value="full_refund">{t('booking_policies.action_full_refund')}</option>
          </Select>
          <Select
            id="invoice-partial"
            label={t('booking_policies.invoice_partial')}
            value={invoicePolicy.partial.action}
            disabled={!canEdit}
            onChange={(e) =>
              setInvoicePolicy((p) => ({
                ...p,
                partial: { action: e.target.value as InvoicePartialAction },
              }))
            }
          >
            <option value="no_refund">{t('booking_policies.action_no_refund')}</option>
            <option value="refund_paid_amount">{t('booking_policies.action_refund_paid')}</option>
            <option value="leave">{t('booking_policies.invoice_leave')}</option>
          </Select>
        </SettingsFormGrid>
        <SettingsHint className="mt-s3">{t('booking_policies.invoice_policy_note')}</SettingsHint>
      </SectionCard>

      <SectionCard
        title={t('settings.billing.subscription')}
        subtitle={t('settings.billing.subscription_managed_externally')}
      >
        <SettingsFormGrid className="mt-0">
          <div>
            <span className={cn(TYPE.label, 'block text-ds-text')}>
              {t('settings.billing.subscription_status')}
            </span>
            <div className="mt-s2">
              <StatusChip tone={subscriptionTone(subscriptionStatus)}>
                {t(`settings.billing.subscription_status_${subscriptionStatus}`)}
              </StatusChip>
            </div>
          </div>
          {clinic.trial_ends_at ? (
            <div>
              <span className={cn(TYPE.label, 'block text-ds-text')}>
                {t('settings.billing.trial_ends_at')}
              </span>
              <span className={cn(TYPE.body, 'mt-s2 inline-block text-ds-text')}>
                {new Date(clinic.trial_ends_at).toLocaleDateString()}
              </span>
            </div>
          ) : null}
        </SettingsFormGrid>
      </SectionCard>

      <SaveBar canEdit={canEdit} pending={update.isPending} />
    </form>
  );
}

function subscriptionTone(status: string) {
  switch (status) {
    case 'active':
      return 'completed' as const;
    case 'trial':
      return 'upcoming' as const;
    case 'paused':
      return 'draft' as const;
    case 'expired':
    case 'cancelled':
      return 'cancelled' as const;
    default:
      return 'neutral' as const;
  }
}
